import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api } from '../lib/api';
import { formatPos } from '../lib/format';
import { Button } from '../ui/controls';
import { ConfirmDialog, RenameDialog } from '../ui/dialog';
import { ErrorNotice, useToast } from '../ui/feedback';
import { Menu } from '../ui/menu';
import { DataTable, PageHead, Tabs } from '../ui/table';
import { DefList, ItemId, Mono, Num, Position, Time, Uuid } from '../ui/values';
import { AreaLabel, AuditTab, BackLink, VariantLabel } from './common';
import { paths } from './paths';
import type { AdminChestDetail } from './types';

const FACING = { north: 'Norden', east: 'Osten', south: 'Süden', west: 'Westen', up: 'oben', down: 'unten' } as const;

/**
 * Kiste (design.md §6): Inhalt als Tabelle (Item-ID · Variante · Menge), keine Slot-Grafik:
 * Slots werden nicht gespeichert, eine Grafik täuschte falsche Genauigkeit vor.
 */
export function ChestDetailPage() {
  const { id = '' } = useParams();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState<'contents' | 'history'>('contents');
  const [action, setAction] = useState<'rename' | 'delete' | null>(null);
  const key = ['admin', 'chest', id];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<AdminChestDetail>(`/admin/chests/${id}`) });
  const c = q.data;

  if (q.isError) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  if (!c) return <p className="muted">Lädt…</p>;
  const title = c.name ?? 'Kiste ohne Namen';
  const total = c.items.reduce((n, i) => n + i.count, 0);

  return (
    <div className="stack-lg">
      <PageHead
        back={<BackLink to={paths.chests} label="Kisten" />}
        title={title}
        context={
          <>
            <span className="mono">{c.server}</span> · <Position dimension={c.dimension} pos={c.pos} />
          </>
        }
        actions={
          <>
            <Button onClick={() => setAction('rename')}>Umbenennen…</Button>
            <Menu items={[{ label: 'Kiste löschen…', danger: true, onSelect: () => setAction('delete') }]} />
          </>
        }
      />

      <DefList
        items={[
          ['Besitzer', <Link to={paths.player(c.owner.id)}>{c.owner.name ?? c.owner.id}</Link>],
          ['Team', c.team ? <Link to={paths.team(c.team.id)}>{c.team.name}</Link> : <span className="muted">–</span>],
          ['Server', <Mono value={c.server} />],
          ['Position', <Position dimension={c.dimension} pos={c.pos} />],
          ['Zweite Hälfte', c.pos2 ? <span className="mono">{formatPos(c.pos2)}</span> : <span className="muted">Einzelkiste</span>],
          ['Block', <ItemId id={c.containerType} />],
          ['Area', <AreaLabel name={c.area} />],
          ['Blickrichtung', c.facing ? FACING[c.facing] : <span className="muted">unbekannt</span>],
          ['Inhalt', <span><Num value={c.items.length} /> Varianten · <Num value={total} /> Items · Version {c.contentsVersion}</span>],
          ['Aktualisiert', c.contentsUpdatedAt ? <Time iso={c.contentsUpdatedAt} absolute /> : <span className="muted">noch nie</span>],
          ['Kisten-ID', <Uuid value={c.id} />],
        ]}
      />

      <div className="stack">
        <Tabs
          active={tab}
          onChange={setTab}
          tabs={[
            { key: 'contents', label: 'Inhalt', count: c.items.length },
            { key: 'history', label: 'Verlauf' },
          ]}
        />
        {tab === 'contents' && (
          <DataTable
            label="Inhalt"
            rows={c.items}
            rowKey={(i) => i.variant.id}
            rowHref={(i) => paths.item(i.item)}
            empty="Die Kiste ist leer oder wurde noch nicht synchronisiert."
            columns={[
              { key: 'item', header: 'Item-ID', cell: (i) => <ItemId id={i.item} /> },
              { key: 'variant', header: 'Variante', cell: (i) => <VariantLabel variant={i.variant} /> },
              { key: 'count', header: 'Menge', num: true, cell: (i) => <Num value={i.count} /> },
            ]}
          />
        )}
        {tab === 'history' && <AuditTab targetType="chest" targetId={id} />}
      </div>

      {action === 'rename' && (
        <RenameDialog
          title="Kiste umbenennen"
          current={c.name}
          maxLength={64}
          allowEmpty
          onClose={() => setAction(null)}
          onSave={async (name, reason) => {
            qc.setQueryData(key, await api.patch<AdminChestDetail>(`/admin/chests/${id}`, { name, reason }));
            await qc.invalidateQueries({ queryKey: ['admin', 'chests'] });
            await qc.invalidateQueries({ queryKey: ['admin', 'audit'] });
            toast('Kiste umbenannt.');
          }}
        />
      )}
      {action === 'delete' && (
        <ConfirmDialog
          open
          title="Kiste löschen"
          consequence={`Löscht „${title}“ von ${c.owner.name ?? 'diesem Spieler'} mit ${c.items.length} Item-Einträgen. Die Mod entfernt sie beim nächsten Abgleich. Nicht umkehrbar.`}
          confirmText={c.name ?? c.id.slice(0, 8)}
          confirmLabel="Löschen"
          onConfirm={async (reason) => {
            await api.delete(`/admin/chests/${id}`, { reason });
            await qc.invalidateQueries({ queryKey: ['admin', 'chests'] });
            toast(`„${title}“ gelöscht.`);
            navigate(paths.chests);
          }}
          onClose={() => setAction(null)}
        />
      )}
    </div>
  );
}
